import React, { useMemo, useRef, useState } from "react";
import ContactIllustration from "../assets/undraw_personal-email.svg";

const MAX_NAME = 80;
const MAX_EMAIL = 120;
const MAX_MESSAGE = 2000;

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());
}

async function safeParseJson(res) {
  const text = await res.text().catch(() => "");
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

const Contact = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  // Honeypot
  const [company, setCompany] = useState("");

  // Time-to-submit
  const mountedAtRef = useRef(Date.now());

  // CRA env var
  const CONTACT_API_URL = useMemo(() => {
    // Prefer env var
    const envUrl = String(process.env.REACT_APP_CONTACT_API_URL || "").trim();

    // Optional fallback (uncomment if you want it hardcoded)
    // const fallback = "https://kuxxib7w1c.execute-api.ap-southeast-2.amazonaws.com/prod/contact";

    return envUrl; // || fallback;
  }, []);

  const sending = status === "sending";

  function resetAlertsOnChange() {
    if (status === "success" || status === "error") {
      setStatus("idle");
      setErrorMsg("");
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    setErrorMsg("");

    const n = name.trim();
    const em = email.trim();
    const msg = message.trim();

    // Client-side validation
    if (!n || n.length > MAX_NAME) return setErrorMsg("Please enter a valid name.");
    if (!em || em.length > MAX_EMAIL || !isValidEmail(em))
      return setErrorMsg("Please enter a valid email.");
    if (!msg || msg.length > MAX_MESSAGE)
      return setErrorMsg("Please enter a valid message.");

    // Bot checks
    if (company) {
      // silently drop
      return;
    }

    const seconds = (Date.now() - mountedAtRef.current) / 1000;
    if (seconds < 3) return setErrorMsg("Please try again.");

    if (!CONTACT_API_URL) {
      return setErrorMsg("Contact service is not configured yet.");
    }

    try {
      setStatus("sending");

      const payload = {
        name: n,
        email: em,
        message: msg,
        // Optional metadata (Lambda currently ignores extra fields; safe to send)
        company,
        ttsSeconds: Math.round(seconds),
        origin: window.location.origin,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
      };

      // Prevent hanging fetches
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const res = await fetch(CONTACT_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify(payload),
        signal: controller.signal,
      }).finally(() => clearTimeout(timeoutId));

      const data = await safeParseJson(res);

      // Your Lambda returns: { success: true }
      const ok = res.ok && data && data.success === true;

      if (!ok) {
        // Try to use returned message, else generic
        const msg =
          data?.message ||
          data?.error ||
          `Failed to send (HTTP ${res.status}). Please try again.`;
        throw new Error(msg);
      }

      setStatus("success");
      setName("");
      setEmail("");
      setMessage("");
      setCompany("");
      mountedAtRef.current = Date.now();
    } catch (err) {
      if (err?.name === "AbortError") {
        setStatus("error");
        setErrorMsg("Request timed out. Please try again.");
        return;
      }
      setStatus("error");
      setErrorMsg(err?.message || "Something went wrong.");
    }
  }

  return (
    <section id="contact" className="bg-gray-50 py-20">
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
        {/* Left: Illustration */}
        <div className="hidden md:block">
          <img
            src={ContactIllustration}
            alt="Contact Illustration"
            className="w-full h-auto"
          />
        </div>

        {/* Right: Form */}
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Contact Us</h2>
          <p className="text-gray-600 mb-10">
            We'd love to hear from you. Fill out the form below and we’ll get back to you soon.
          </p>

          <form
            onSubmit={onSubmit}
            className="space-y-6 bg-white p-8 rounded-xl shadow-md border border-gray-200"
          >
            {/* Honeypot (hidden from humans) */}
            <div className="hidden">
              <label htmlFor="company">Company</label>
              <input
                id="company"
                name="company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                autoComplete="off"
                tabIndex={-1}
              />
            </div>

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                maxLength={MAX_NAME}
                value={name}
                disabled={sending}
                onChange={(e) => {
                  resetAlertsOnChange();
                  setName(e.target.value);
                }}
                placeholder="Your full name"
                className="w-full border border-gray-300 rounded-md px-4 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                maxLength={MAX_EMAIL}
                value={email}
                disabled={sending}
                onChange={(e) => {
                  resetAlertsOnChange();
                  setEmail(e.target.value);
                }}
                placeholder="your@email.com"
                className="w-full border border-gray-300 rounded-md px-4 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                rows="5"
                required
                maxLength={MAX_MESSAGE}
                value={message}
                disabled={sending}
                onChange={(e) => {
                  resetAlertsOnChange();
                  setMessage(e.target.value);
                }}
                placeholder="Tell us what you're looking for..."
                className="w-full border border-gray-300 rounded-md px-4 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
              />
              <div className="mt-1 text-xs text-gray-500">
                {message.length}/{MAX_MESSAGE}
              </div>
            </div>

            {status === "success" && (
              <div className="rounded-md bg-green-50 border border-green-200 px-4 py-3 text-green-800 text-sm">
                Thanks for contacting Badami Labs! We’ll get back to you soon.
              </div>
            )}

            {errorMsg && (
              <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-red-800 text-sm">
                {errorMsg}
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={sending}
                className="bg-[#4C47DC] disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-2 rounded-md hover:bg-[#3b39b2] transition duration-200"
              >
                {sending ? "Sending..." : "Send Message"}
              </button>
            </div>
          </form>

          <p className="mt-4 text-xs text-gray-500">
            We never store your message publicly. For urgent matters, email us directly.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Contact;